import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { AgentCard } from '@/components/dashboard/AgentCard'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassButton } from '@/components/ui/GlassButton'
import { GlassInput, GlassTextarea } from '@/components/ui/GlassInput'
import { agentsQueryOptions, providersQueryOptions, offeringPacksQueryOptions } from '@/lib/queryOptions'
import { api } from '@/lib/api'
import { Plus, X } from 'lucide-react'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/dashboard/agents/')({
  beforeLoad: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(agentsQueryOptions()).catch(() => {
      throw redirect({ to: '/auth/login' })
    })
  },
  component: AgentsPage,
})

function AgentsPage() {
  const t = useT()
  const qc = useQueryClient()
  const { data: agents = [] } = useQuery(agentsQueryOptions())
  const { data: providers = [] } = useQuery(providersQueryOptions())
  const { data: offeringPacks = [] } = useQuery(offeringPacksQueryOptions())
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [aiConfigId, setAiConfigId] = useState('')
  const [offeringPackId, setOfferingPackId] = useState('')
  const [formError, setFormError] = useState('')

  const createMutation = useMutation({
    mutationFn: api.agents.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents'] })
      setShowCreate(false)
      setName(''); setDescription(''); setSystemPrompt(''); setAiConfigId(''); setOfferingPackId('')
    },
    onError: (err) => setFormError(err.message),
  })

  const agentCountLabel = agents.length === 1
    ? t('agents.list.agentCountSingular', { count: agents.length })
    : t('agents.list.agentCountPlural', { count: agents.length })

  return (
    <RootLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">{t('agents.list.title')}</h1>
            <p className="text-sm text-white/40 mt-1">{agentCountLabel}</p>
          </div>
          <GlassButton size="sm" onClick={() => setShowCreate(true)}>
            <Plus size={14} />
            {t('agents.list.newAgent')}
          </GlassButton>
        </div>

        {/* Create form */}
        {showCreate && (
          <GlassCard glow className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-white">{t('agents.list.newAgentTitle')}</h2>
              <button onClick={() => setShowCreate(false)} className="text-white/40 hover:text-white">
                <X size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <GlassInput
                label={t('agents.list.nameLabel')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('agents.list.namePlaceholder')}
                required
              />
              <GlassInput
                label={t('agents.list.descriptionLabel')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('agents.list.descriptionPlaceholder')}
              />
            </div>
            <GlassTextarea
              label={t('agents.list.systemPromptLabel')}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder={t('agents.list.systemPromptPlaceholder')}
              rows={4}
            />
            <div>
              <label className="text-sm font-medium text-white/70 block mb-1.5">{t('agents.list.offeringPackLabel')}</label>
              <select
                value={offeringPackId}
                onChange={(e) => {
                  const selectedId = e.target.value
                  setOfferingPackId(selectedId)
                  const pack = offeringPacks.find((p) => p.id === selectedId)
                  if (pack?.defaultSystemPrompt && !systemPrompt) {
                    setSystemPrompt(pack.defaultSystemPrompt)
                  }
                }}
                className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              >
                <option value="">{t('agents.list.offeringPackPlaceholder')}</option>
                {offeringPacks.map((pack) => (
                  <option key={pack.id} value={pack.id} style={{ background: '#1a1a2e' }}>
                    {pack.name} ({pack.audience} · {pack.vertical})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-white/70 block mb-1.5">{t('agents.list.providerLabel')}</label>
              <select
                value={aiConfigId}
                onChange={(e) => setAiConfigId(e.target.value)}
                className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              >
                <option value="">{t('agents.list.selectProvider')}</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id} style={{ background: '#1a1a2e' }}>
                    {p.name} ({p.providerId} · {p.modelId})
                  </option>
                ))}
              </select>
            </div>
            {formError && <p className="text-red-400 text-sm">{formError}</p>}
            <div className="flex gap-2 justify-end">
              <GlassButton variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
                {t('common.cancel')}
              </GlassButton>
              <GlassButton
                size="sm"
                loading={createMutation.isPending}
                onClick={() => createMutation.mutate({
                  name,
                  description,
                  systemPrompt,
                  aiConfigId: aiConfigId || undefined,
                  metadata: offeringPackId ? { offeringPackId } : undefined,
                })}
                disabled={!name}
              >
                {t('agents.list.createAgent')}
              </GlassButton>
            </div>
          </GlassCard>
        )}

        {/* Agent grid */}
        {agents.length === 0 ? (
          <GlassCard className="text-center py-16 space-y-3">
            <p className="text-white/40">{t('agents.list.noAgents')}</p>
            <GlassButton size="sm" onClick={() => setShowCreate(true)}>
              {t('agents.list.createFirstAgent')}
            </GlassButton>
          </GlassCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {agents.map((agent) => (
              <AgentCard key={agent.id} agent={agent} />
            ))}
          </div>
        )}
      </div>
    </RootLayout>
  )
}
